import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FaPlus } from "react-icons/fa";

import {
  VideoDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";

import { LanguageService } from "../../services/languages.service";
import { VideosService } from "../../services/videos.service";

import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";

const DEFAULT_LANG = "UZ";
const STATIC_TIME = "01:00"; // ✅ backend üçün statik time (UI-də yoxdur)

type FormTranslation = {
  lang_code: string;
  title: string;
  time: string; // backend tələb edir deyə saxlayırıq
};

type FormValues = {
  type: "home" | "about";
  title: string;
  time: string; // UI-də olmayacaq, submitdə STATIC_TIME göndərəcəyik
  cover_image: string; // base64 string (SelectImage verir) / editdə url ola bilər
  video_file: File | null; // ✅ file
  translations: FormTranslation[];
};

const Video = () => {
  const [videos, setVideos] = useState<VideoDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<VideoDataType | null>(
    null
  );

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState<string>(DEFAULT_LANG);

  const [logoFileList, setLogoFileList] = useState<any[]>([]);

  // video file UI
  const [videoName, setVideoName] = useState<string>("");

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const normalize = (c: string) => String(c || "").toLowerCase();

  const loadVideos = async () => {
    setLoading(true);
    try {
      const response = await VideosService.getAllVideos();
      if (Array.isArray(response?.data?.data)) {
        const dataWithKeys = response.data.data.map(
          (item: VideoDataType, index: number) => ({
            ...item,
            key: index + 1,
          })
        );
        setVideos(dataWithKeys);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAfterSubmit = async () => {
    setIsModalVisible(false);
    resetForm();
    setLangCode(DEFAULT_LANG);
    setLogoFileList([]);
    setVideoName("");
    await loadVideos();
  };

  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik<FormValues>({
      initialValues: {
        type: "home",
        title: "",
        time: STATIC_TIME, // UI-də dəyişmir, default saxlayırıq
        cover_image: "",
        video_file: null,
        translations: [],
      },
      validationSchema: Yup.object({
        type: Yup.string()
          .oneOf(["home", "about"], "Yalnız home və ya about seçilə bilər")
          .required("Video tipi tələb olunur"),
        title: Yup.string().required("Video başlığı tələb olunur"),
        cover_image: Yup.string().required("Videonun şəkili tələb olunur"),
        video_file: Yup.mixed<File>()
          .nullable()
          .test("videoRequired", "Video faylı tələb olunur", function (val) {
            // ✅ add zamanı mütləq, edit zamanı istəyə bağlı
            if (!isEditing) return !!val;
            return true;
          }),
      }),
      onSubmit: async (vals) => {
        setLoading(true);
        try {
          const def = normalize(DEFAULT_LANG);

          // ✅ default translation (root title + statik time)
          const defaultTranslation: FormTranslation = {
            lang_code: def,
            title: vals.title,
            time: STATIC_TIME,
          };

          // ✅ translations array: default + others (hamısına statik time)
          const mergedTranslations: FormTranslation[] = [
            defaultTranslation,
            ...(vals.translations || []).filter(
              (t) => normalize(t.lang_code) !== def
            ),
          ].map((t) => ({
            lang_code: normalize(t.lang_code),
            title: t.title || "",
            time: STATIC_TIME,
          }));

          // ✅ FormData (Postman formatı kimi)
          const fd = new FormData();
          fd.append("type", vals.type);
          fd.append("title", vals.title);
          fd.append("time", STATIC_TIME); // ✅ statik
          fd.append("cover_image", vals.cover_image); // base64 və ya url

          // ✅ video_file yalnız seçilibsə əlavə et
          if (vals.video_file) {
            fd.append("video_file", vals.video_file);
          }

          // ✅ translations[0][lang_code] ...
          mergedTranslations.forEach((t, idx) => {
            fd.append(`translations[${idx}][lang_code]`, t.lang_code);
            fd.append(`translations[${idx}][title]`, t.title);
            fd.append(`translations[${idx}][time]`, t.time); // ✅ statik
          });

          let response;
          if (isEditing && editingRecord) {
            // ✅ service səndə: updateVideo(payload:any)
            // burda payload = { id, ... } olmalı idi; amma sən artıq FormData göndərmək istəyirsən
            // sənin son yazdığın variantda: VideosService.updateVideo(id, fd) çağırmısan.
            // Ona görə burada da elə saxlayıram:
            response = await VideosService.updateVideo(editingRecord.id, fd);

            if (response.status === 200) {
              message.success("Video uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Videonu yeniləmək alınmadı."
              );
            }
          } else {
            response = await VideosService.addVideo(fd);

            if (response.status === 201 || response.status === 200) {
              message.success("Video uğurla əlavə edildi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Video əlavə etmək alınmadı."
              );
            }
          }
        } catch (e: any) {
          console.log(e);
          message.error(e?.response?.data?.message || "Xəta baş verdi.");
        } finally {
          setLoading(false);
        }
      },
    });

  // ✅ title dilə görə yazılır (time UI-də yoxdur)
  const handleTitleChange = (value: string) => {
    const lc = normalize(langCode);
    const def = normalize(DEFAULT_LANG);

    if (lc === def) {
      setFieldValue("title", value);
      return;
    }

    const updated = [...(values.translations || [])];
    const idx = updated.findIndex((t) => normalize(t.lang_code) === lc);

    if (idx !== -1) {
      updated[idx] = { ...updated[idx], title: value, time: STATIC_TIME };
    } else {
      updated.push({
        lang_code: lc,
        title: value,
        time: STATIC_TIME,
      });
    }

    setFieldValue("translations", updated);
  };

  const handleDeleteClick = async (record: VideoDataType) => {
    try {
      await VideosService.deleteVideo(record.id.toString());
      setVideos((prev) => prev.filter((x) => x.id !== record.id));
      message.success("Video uğurla silindi!");
    } catch {
      message.error("Videonu silmək alınmadı.");
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);

    resetForm();
    setLangCode(DEFAULT_LANG);

    setFieldValue("type", "home");
    setFieldValue("cover_image", "");
    setFieldValue("video_file", null);
    setFieldValue("time", STATIC_TIME);

    setLogoFileList([]);
    setVideoName("");
  };

  const handleEditClick = (record: VideoDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    setLangCode(DEFAULT_LANG);

    setFieldValue("type", record.type || "home");
    setFieldValue("title", record.title || "");

    // time UI-də yoxdur, amma formda saxlayırıq
    setFieldValue("time", STATIC_TIME);

    // cover_image backenddən URL gəlir -> editdə dəyişməsən belə qalır
    setFieldValue("cover_image", record.cover_image || "");

    // editdə video_file seçilməyə bilər
    setFieldValue("video_file", null);
    setVideoName("");

    // translations backenddən gələn kimi (əgər t.time yoxdursa, submitdə STATIC_TIME basılacaq)
    setFieldValue("translations", record.translations || []);

    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.title} cover_image`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : []
    );
  };

  // table-də video linkini göstərmək üçün fallback-lar
  const getVideoUrl = (record: any) =>
    record?.video_file ||
    record?.video ||
    record?.file ||
    record?.video_url ||
    record?.link ||
    "";

  const columns: TableProps<VideoDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a: any, b: any) => (a.key || 0) - (b.key || 0),
    },
    {
      title: "Videonun başlığı",
      dataIndex: "title",
      sorter: (a: any, b: any) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      filters: [
        { text: "Home", value: "home" },
        { text: "About", value: "about" },
      ],
      onFilter: (value, record) =>
        String(record.type || "")
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      render: (_: any, record: VideoDataType) => {
        const t = String(record.type || "").toLowerCase();
        const isAbout = t === "about";
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "6px 10px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 600,
              border: `1px solid ${isAbout ? "#48CAE4" : "#0277bd"}`,
              color: isAbout ? "#48CAE4" : "#0277bd",
              background: isAbout
                ? "rgba(9, 115, 177, 0.06)"
                : "rgba(2, 119, 189, 0.06)",
            }}
          >
            {isAbout ? "About" : "Home"}
          </span>
        );
      },
    },
    {
      title: "Video",
      dataIndex: "video_file",
      render: (_: any, record: any) => {
        const url = getVideoUrl(record);
        return url ? (
          <a href={url} target="_blank" rel="noreferrer">
            Videonu aç
          </a>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_: any, record: any) => (
        <div
          style={{ cursor: record.cover_image ? "pointer" : "default" }}
          onClick={() => {
            if (record.cover_image) {
              setSelectedImage(record.cover_image);
              setShowImageModal(true);
            }
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt="cover"
            />
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_: any, record: any) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu videonu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const currentTitle =
    normalize(langCode) === normalize(DEFAULT_LANG)
      ? values.title
      : values.translations.find(
          (t) => normalize(t.lang_code) === normalize(langCode)
        )?.title || "";

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Video Kontentlər</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Video Kontentlər</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>

            <div className="pages_table">
              <Table<VideoDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={videos}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Videonu redaktə edin" : "Yeni video əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={(c: string) => setLangCode(normalize(c))}
            values={values as any}
            setFieldValue={setFieldValue}
            langs={langs}
          />

          <div className="form_input">
            <label className="label" htmlFor="type">
              Video tipi
            </label>
            <div className="input_wrapper">
              <select
                id="type"
                name="type"
                value={values.type}
                onChange={(e) =>
                  setFieldValue("type", e.target.value as FormValues["type"])
                }
              >
                <option value="home">Home</option>
                <option value="about">About</option>
              </select>
              <span className="focus_border"></span>
            </div>

            <ErrorMessage error={errors.type as any} touched={touched.type} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="title">
              Videonun başlığı ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="title"
                name="title"
                placeholder="Videonun başlığı"
                value={currentTitle}
                onChange={(e) => handleTitleChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>

            {normalize(langCode) === normalize(DEFAULT_LANG) && (
              <ErrorMessage error={errors.title} touched={touched.title} />
            )}
          </div>

          {/* ✅ Video upload */}
          <div className="form_input">
            <label className="label">Video faylı</label>
            <div className="input_wrapper">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0] || null;
                  setFieldValue("video_file", file);
                  setVideoName(file?.name || "");
                }}
              />
              <span className="focus_border"></span>
            </div>

            {videoName ? (
              <small style={{ display: "block" }}>{videoName}</small>
            ) : null}

            <ErrorMessage
              error={errors.video_file as any}
              touched={touched.video_file as any}
            />

            {isEditing ? (
              <small style={{ display: "block", opacity: 0.7 }}>
                Redaktədə video seçməsən, köhnə video qalacaq.
              </small>
            ) : null}
          </div>

          {/* ✅ cover_image base64 */}
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="cover_image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage
              error={errors.cover_image}
              touched={touched.cover_image}
            />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Video;
